import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";

import { domainKeys } from "~/components/siteSpeed/queryKeys";
import { PAGE_TITLES } from "~/configs/pageTitles";
import { useTeam } from "~/contexts/TeamProvider.tsx";
import { usePageTitle } from "~/hooks/usePageTitle";
import { routes } from "~/router";

import { apiClient } from "../../../api/ApiClient";
import SiteSpeedTable from "../../../components/siteSpeed/SiteSpeedTable";
import { PaginationConfig } from "../../../components/ui/pagination/Pagination";
import { useDebounce } from "../../../hooks/useDebounce";
import styles from "./SiteSpeedPage.module.scss";

const SiteSpeedPage = () => {
  usePageTitle(PAGE_TITLES.domains);
  const { team } = useTeam();

  const [pagination, setPagination] = useState<PaginationConfig>({
    currentPage: 1,
    itemsPerPage: 10,
    nextPage: null,
    prevPage: null,
    totalPages: 1,
    totalItems: 10,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const {
    data: domains,
    isLoading: isDomainsLoading,
    isFetching,
  } = useQuery({
    queryKey: domainKeys.list({
      page: pagination.currentPage,
      perPage: pagination.itemsPerPage,
      q: debouncedSearchTerm,
    }),
    queryFn: async () => {
      const response = await apiClient.domains.domainsList(
        {
          "page[size]": pagination.itemsPerPage,
          "page[number]": pagination.currentPage,
          q: {
            // @ts-expect-error BE types - missing input field
            name_cont: debouncedSearchTerm,
          },
        },

        {
          headers: {
            "Authorize-Member-Actions": "destroy,update,show,status",
          },
        },
      );
      const { headers } = response;

      setPagination((prev: PaginationConfig) => ({
        ...prev,
        totalItems: Number(headers.get("x-pager-total-count") || 0),
        nextPage: Number(headers.get("x-pager-next-page") || 0),
        prevPage: Number(headers.get("x-pager-prev-page") || 0),
        totalPages: Number(headers.get("x-pager-total-pages") || 1),
        currentPageAfterFetch: Number(headers.get("x-pager-current-page") || 1),
      }));

      return response?.data;
    },
  });
  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }));
  }, [debouncedSearchTerm]);

  if (team && !team.subscription?.price?.product?.prerendering) {
    return <Navigate to={routes.home()} replace />;
  }

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{PAGE_TITLES.domains}</h1>
      <SiteSpeedTable
        data={domains?.domains || []}
        // @ts-expect-error BE types are not correct
        authorizations={domains?.authorizations || []}
        pagination={pagination}
        setPagination={setPagination}
        isLoading={isDomainsLoading}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        isFetching={isFetching}
      />
    </div>
  );
};
export default SiteSpeedPage;
